import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEOHead from '../components/SEOHead';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { 
  ArrowRight, CheckCircle2, Star, Trophy, Zap,
  Code2, Search, Wrench
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comparison page data with SEO content
const comparisonPages = {
  'best-free-online-tools': {
    id: 'best-free-online-tools',
    slug: 'best-free-online-tools',
    icon: 'trophy',
    seoTitle: 'Best Free Online Tools (2026) - 180+ Utilities Compared',
    seoDescription: 'Discover the best free online tools for 2026. 180+ utilities for developers, designers & creators. No signup required. Updated for 2026.',
    keywords: 'best free online tools, top free tools 2026, best web tools, free utilities online, best developer tools, productivity tools',
    heroTitle: 'Best Free Online Tools 2026',
    heroSubtitle: 'Our curated selection of the most useful free tools for developers, designers, and everyday users',
    introduction: `Looking for the best free online tools in 2026? We've compiled a comprehensive list of the most useful, reliable, and feature-rich tools available on the web. *Updated for 2026 with the latest utilities and features.*

What makes a tool "the best"? We evaluate based on ease of use, feature completeness, performance, privacy (all our tools process data locally), and user feedback. Each tool on this list has been tested and refined to provide the best possible experience.

Whether you're a developer debugging code, a content creator crafting the perfect post, or someone who just needs to generate a secure password, this guide will help you find the right tool for your needs.`,
    seoContent: `Finding quality free online tools can be challenging. Many sites are cluttered with ads, require unnecessary signups, or send your data to unknown servers. ToolsForge is different - every tool on our platform is designed with privacy and simplicity in mind.

Our collection includes essential utilities that professionals use daily. From the ever-popular JSON Formatter that helps developers work with API responses, to the Password Generator that creates cryptographically secure passwords, each tool serves a specific purpose and does it exceptionally well.

The tools listed below represent the cream of the crop in their respective categories. We've chosen them based on user ratings, frequency of use, and the quality of results they produce. All tools work directly in your browser, ensuring your data never leaves your device.`,
    tools: [
      'password-generator',
      'json-formatter',
      'text-counter',
      'base64-encoder',
      'uuid-generator',
      'hash-generator',
      'url-encoder',
      'case-converter',
      'timestamp-converter',
      'color-picker'
    ],
    toolHighlights: {
      'password-generator': 'Most popular security tool - generates cryptographically secure passwords',
      'json-formatter': 'Essential for developers - format, validate, and beautify JSON instantly',
      'text-counter': 'Perfect for writers - count words, characters, and paragraphs in real-time',
      'base64-encoder': 'Universal encoding tool - encode and decode Base64 for data transmission',
      'uuid-generator': 'Database essential - generate unique identifiers for any project',
      'hash-generator': 'Security & verification - create SHA-256 and other hash signatures',
      'url-encoder': 'Web development must-have - properly encode URLs for APIs and links',
      'case-converter': '9 case formats - convert between camelCase, snake_case, and more',
      'timestamp-converter': 'Time utility - convert between Unix timestamps and human dates',
      'color-picker': 'Designer favorite - pick colors and get HEX, RGB, HSL values instantly'
    },
    faqs: [
      {
        q: 'Are all these tools really free?',
        a: 'Yes, every tool listed is 100% free with no signup required. We offer premium features for power users, but the core tools are always free.'
      },
      {
        q: 'Do I need to create an account to use these tools?',
        a: 'No account is needed for any tool. You can start using them immediately. Creating a free account lets you save favorites and track usage.'
      },
      {
        q: 'Is my data safe when using these tools?',
        a: 'Absolutely. All processing happens locally in your browser. Your data is never sent to our servers or stored anywhere.'
      },
      {
        q: 'How often are new tools added?',
        a: 'We regularly add new tools based on user feedback and industry needs. Check back often or subscribe to our blog for updates.'
      },
      {
        q: 'Can I use these tools on mobile devices?',
        a: 'Yes, all tools are fully responsive and work great on phones, tablets, and desktops.'
      },
      {
        q: 'What makes these tools better than alternatives?',
        a: 'Privacy-first design, clean interfaces, no ads interrupting your workflow, and continuous improvements based on user feedback.'
      }
    ],
    relatedBlogPosts: [
      'best-free-online-tools-2026',
      'developer-utilities-guide',
      'secure-password-generator-guide'
    ],
    relatedCategories: ['developer-tools', 'generators', 'converters']
  },
  'best-developer-tools': {
    id: 'best-developer-tools',
    slug: 'best-developer-tools',
    icon: 'code',
    seoTitle: 'Best Developer Tools (2026) - Free Programming Utilities',
    seoDescription: 'Top free developer tools for 2026. JSON formatter, regex tester, hash generator & more. No installation required. Updated for 2026.',
    keywords: 'best developer tools 2026, free programming tools, developer utilities, json formatter, regex tester, code tools, programming utilities online',
    heroTitle: 'Best Free Developer Tools 2026',
    heroSubtitle: 'Essential utilities every developer needs, all free and available in your browser',
    introduction: `Developers need reliable tools that just work in 2026. No complicated setups, no heavy IDEs for simple tasks, just quick and efficient utilities that streamline your workflow. *Updated for 2026 with the latest developer utilities.*

Every tool in this collection is designed to solve a specific problem developers face daily. Need to quickly format an API response? Our JSON Formatter handles it. Testing a complex regex pattern? The Regex Tester shows matches in real-time. Converting timestamps between formats? Done in seconds.

The best part? All tools run entirely in your browser. Your code, API keys, and sensitive data never leave your machine. That's the privacy-first approach developers deserve.`,
    seoContent: `Developer productivity depends on having the right tools at hand. While full-featured IDEs are great for complex projects, sometimes you need a quick utility that does one thing exceptionally well. That's where ToolsForge comes in.

Our developer tools are built by developers, for developers. We understand the frustration of bloated tools, unnecessary signups, and data being sent to unknown servers. Every tool in our collection prioritizes simplicity, speed, and privacy.

The JSON Formatter alone handles thousands of requests daily from developers working with APIs. Combined with our JSON Validator, Regex Tester, and Hash Generator, you have a complete toolkit for everyday development tasks. Add the UUID Generator for database work and the Timestamp Converter for handling dates, and you've covered most common utility needs.

These tools integrate seamlessly into your workflow. Bookmark them, keep them in a tab, or use them whenever the need arises. No downloads, no installations, no accounts required.`,
    tools: [
      'json-formatter',
      'json-validator',
      'regex-tester',
      'hash-generator',
      'uuid-generator',
      'timestamp-converter',
      'base64-encoder',
      'url-encoder',
      'html-minifier',
      'css-minifier',
      'js-minifier',
      'diff-checker'
    ],
    toolHighlights: {
      'json-formatter': 'Format and beautify JSON with syntax validation and error highlighting',
      'json-validator': 'Validate JSON syntax and view structure statistics (depth, items, size)',
      'regex-tester': 'Test patterns with real-time matching, flags support, and group capture',
      'hash-generator': 'Generate SHA-1, SHA-256, SHA-384, and SHA-512 hashes securely',
      'uuid-generator': 'Create single or bulk UUID v4 identifiers for databases and systems',
      'timestamp-converter': 'Convert between Unix timestamps and human-readable dates instantly',
      'base64-encoder': 'Encode and decode Base64 with full Unicode support',
      'url-encoder': 'Percent-encode URLs and query parameters for safe transmission',
      'html-minifier': 'Compress HTML by removing whitespace and comments',
      'css-minifier': 'Minify CSS for production with size reduction stats',
      'js-minifier': 'Compress JavaScript and beautify minified code',
      'diff-checker': 'Compare code side-by-side with additions, deletions, and changes highlighted'
    },
    faqs: [
      {
        q: 'Are these tools suitable for production use?',
        a: 'Yes, the output is production-ready. For large-scale automation, you might want to integrate equivalent libraries into your build process.'
      },
      {
        q: 'Do the tools work offline?',
        a: 'Once loaded, most tools work offline since all processing happens in your browser. No internet needed for the actual operations.'
      },
      {
        q: 'What programming languages are supported?',
        a: 'Our tools work with any language. JSON/regex tools use JavaScript standards, which are compatible with most languages.'
      },
      {
        q: 'Is there an API available?',
        a: 'Currently all tools are browser-based. For programmatic access, we recommend using equivalent libraries in your preferred language.'
      },
      {
        q: 'How do I report bugs or suggest features?',
        a: 'We welcome feedback! Use the contact form on our website or reach out through our blog comments.'
      },
      {
        q: 'Why browser-based instead of downloadable apps?',
        a: 'Browser tools mean no installation, automatic updates, cross-platform compatibility, and guaranteed privacy since data stays local.'
      }
    ],
    relatedBlogPosts: [
      'developer-utilities-guide',
      'how-to-format-json-complete-guide',
      'regex-tutorial-beginners',
      'free-tools-programmers'
    ],
    relatedCategories: ['converters', 'generators', 'text-tools']
  },
  'best-seo-tools': {
    id: 'best-seo-tools',
    slug: 'best-seo-tools',
    icon: 'search',
    seoTitle: 'Best Free SEO Tools (2026) - Boost Your Rankings',
    seoDescription: 'Top free SEO tools for 2026. Meta tag generator, slug generator & more. Improve your search rankings today. Updated for 2026.',
    keywords: 'best seo tools 2026, free seo tools, meta tag generator, slug generator, seo optimization tools, website seo, search engine optimization',
    heroTitle: 'Best Free SEO Tools 2026',
    heroSubtitle: 'Optimize your website for search engines with these essential free tools',
    introduction: `Search engine optimization doesn't have to be complicated or expensive in 2026. With the right tools, you can implement SEO best practices yourself. *Updated for 2026 with the latest SEO techniques.*

Good SEO starts with the basics: optimized titles and descriptions, clean URLs, properly structured content, and correct encoding. These tools help you nail the fundamentals without hiring an expensive consultant or subscribing to costly SEO suites.

Whether you're launching a new website, optimizing existing content, or learning SEO from scratch, these tools provide immediate value. See the impact of your optimizations in real-time.`,
    seoContent: `SEO success comes from consistent application of proven techniques. While advanced SEO involves many factors, getting the basics right can significantly improve your search visibility. That's where our SEO tools excel.

The Meta Tag Generator creates complete meta tags including Open Graph and Twitter Cards, ensuring your content looks great both in search results and when shared on social media. Pair this with the Slug Generator to create clean, keyword-rich URLs that both users and search engines love.

Content optimization is equally important. The Text Counter helps you hit optimal content lengths - search engines favor comprehensive content, typically 1,500+ words for competitive topics. Combined with proper keyword placement and structure, you're set for success.

URL encoding might seem minor, but incorrect URLs can hurt your SEO. Our URL Encoder ensures all special characters are properly handled, preventing broken links and indexing issues. Small details like these add up to significant ranking improvements.

Remember: SEO is a marathon, not a sprint. Use these tools consistently as you create and optimize content, and you'll see steady improvements in your search rankings.`,
    tools: [
      'meta-tag-generator',
      'slug-generator',
      'url-encoder',
      'text-counter',
      'html-encoder',
      'html-minifier',
      'json-formatter',
      'lorem-ipsum-generator'
    ],
    toolHighlights: {
      'meta-tag-generator': 'Create complete SEO meta tags including Open Graph and Twitter Cards',
      'slug-generator': 'Generate clean, SEO-friendly URL slugs from any text',
      'url-encoder': 'Properly encode URLs to prevent broken links and indexing issues',
      'text-counter': 'Track content length for optimal SEO - aim for comprehensive coverage',
      'html-encoder': 'Safely encode special characters in HTML content',
      'html-minifier': 'Compress HTML for faster page loads - a key ranking factor',
      'json-formatter': 'Format structured data (JSON-LD) for rich search results',
      'lorem-ipsum-generator': 'Create placeholder content while building SEO-optimized pages'
    },
    faqs: [
      {
        q: 'How do meta tags affect my SEO?',
        a: 'Meta tags (title, description) appear in search results and influence click-through rates. Good meta tags can significantly improve traffic even without changing rankings.'
      },
      {
        q: 'What makes a URL "SEO-friendly"?',
        a: 'Short, descriptive URLs with keywords separated by hyphens. Avoid numbers, special characters, and excessive depth. Our Slug Generator handles this automatically.'
      },
      {
        q: 'How long should my content be for good SEO?',
        a: 'It varies by topic, but comprehensive content (1,500+ words for competitive topics) typically ranks better. Quality matters more than length, but depth shows expertise.'
      },
      {
        q: 'Do these tools replace paid SEO software?',
        a: 'For content and technical optimization basics, yes. For backlink analysis, competitor research, and rank tracking, you may still need specialized tools.'
      },
      {
        q: 'How quickly will I see SEO results?',
        a: 'SEO improvements typically take 2-6 months to show in rankings. Technical fixes (like proper meta tags) may have faster effects.'
      },
      {
        q: 'Should I use all these tools for every page?',
        a: 'At minimum, use the Meta Tag Generator and Slug Generator for every page. Other tools help with specific optimization tasks.'
      }
    ],
    relatedBlogPosts: [
      'url-slug-generator-seo',
      'minify-html-css-guide',
      'best-free-online-tools-2026'
    ],
    relatedCategories: ['text-tools', 'developer-tools', 'converters']
  }
};

const iconMap = {
  trophy: Trophy,
  code: Code2,
  search: Search,
};

const ComparisonPage = () => {
  const { slug } = useParams();
  const [tools, setTools] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const page = comparisonPages[slug];

  useEffect(() => {
    const fetchData = async () => {
      if (!page) {
        setLoading(false);
        return;
      }

      try {
        const toolsResponse = await axios.get(`${API}/tools`);
        const pageTools = toolsResponse.data.filter(t => 
          page.tools.includes(t.slug)
        );
        // Sort by page.tools order
        const sortedTools = page.tools
          .map(slug => pageTools.find(t => t.slug === slug))
          .filter(Boolean);
        setTools(sortedTools);

        const blogResponse = await axios.get(`${API}/blog`);
        const relatedPosts = blogResponse.data.filter(p => 
          page.relatedBlogPosts.includes(p.slug)
        );
        setBlogPosts(relatedPosts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, page]);

  // Generate ItemList structured data
  const generateItemListSchema = () => ({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": page?.heroTitle,
    "description": page?.seoDescription,
    "numberOfItems": tools.length,
    "itemListElement": tools.map((tool, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": tool.name,
        "description": tool.short_description,
        "url": `${window.location.origin}/tools/${tool.slug}`,
        "applicationCategory": "WebApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      }
    }))
  });

  // Generate FAQPage structured data
  const generateFAQSchema = () => ({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page?.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  });

  // Generate BreadcrumbList structured data
  const generateBreadcrumbSchema = () => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "All Tools",
        "item": `${window.location.origin}/all-tools`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": page?.heroTitle,
        "item": `${window.location.origin}/compare/${slug}`
      }
    ]
  });

  // Generate Article structured data
  const generateArticleSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": page?.heroTitle,
    "description": page?.seoDescription,
    "url": `${window.location.origin}/compare/${slug}`,
    "publisher": {
      "@type": "Organization",
      "name": "ToolsForge",
      "url": window.location.origin
    },
    "datePublished": "2026-01-01",
    "dateModified": new Date().toISOString().split('T')[0]
  });

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-4">Page Not Found</h1>
          <p className="text-muted-foreground mb-6">The comparison page you're looking for doesn't exist.</p>
          <Link to="/all-tools">
            <Button>Browse All Tools</Button>
          </Link>
        </div>
      </div>
    );
  }

  const PageIcon = iconMap[page.icon] || Wrench;

  return (
    <>
      <SEOHead
        title={page.seoTitle}
        description={page.seoDescription}
        keywords={page.keywords}
        canonical={`${window.location.origin}/compare/${slug}`}
      />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateItemListSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFAQSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateBreadcrumbSchema()) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleSchema()) }} />

      <div className="min-h-screen pt-24 pb-16 px-6" data-testid={`comparison-page-${slug}`}>
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <Link to="/all-tools" className="hover:text-foreground transition-colors">All Tools</Link>
            <span>/</span>
            <span className="text-foreground">{page.heroTitle}</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-6">
              <PageIcon className="w-8 h-8 text-amber-500" />
            </div>
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-amber-500/30 text-amber-500">
              <Star className="w-3 h-3 mr-2" />
              Top {tools.length} Picks
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              {page.heroTitle}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {page.heroSubtitle}
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="prose prose-invert max-w-4xl mx-auto mb-16"
          >
            {page.introduction.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-muted-foreground leading-relaxed text-base">
                {paragraph}
              </p>
            ))}
          </motion.div>

          {/* Tools Ranking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-8 font-['Plus_Jakarta_Sans'] flex items-center gap-3">
              <Trophy className="w-6 h-6 text-amber-500" />
              Our Top Picks
            </h2>
            <div className="space-y-4">
              {tools.map((tool, index) => (
                <motion.div
                  key={tool.slug}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="group flex items-start gap-4 p-5 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                    data-testid={`comparison-tool-${tool.slug}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      index === 0 ? 'bg-amber-500/20 text-amber-500' :
                      index === 1 ? 'bg-slate-400/20 text-slate-400' :
                      index === 2 ? 'bg-orange-600/20 text-orange-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      <span className="font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                          {tool.name}
                        </h3>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${
                            tool.tier === 'free' 
                              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                              : 'bg-primary/10 text-primary border-primary/30'
                          }`}
                        >
                          {tool.tier}
                        </Badge>
                      </div>
                      <p className="text-muted-foreground mb-2">
                        {page.toolHighlights?.[tool.slug] || tool.short_description}
                      </p>
                      <div className="flex items-center text-primary text-sm">
                        Try This Tool <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Extended SEO Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-16 p-8 rounded-2xl bg-muted/30 border border-border"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Why These Tools Stand Out
            </h2>
            <div className="prose prose-invert max-w-none">
              {page.seoContent.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-muted-foreground leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </div>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="grid md:grid-cols-3 gap-6 mb-16"
          >
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-3" />
              <h3 className="font-semibold mb-2">100% Free Forever</h3>
              <p className="text-sm text-muted-foreground">
                All tools on this list are completely free. No hidden fees, no premium locks on essential features.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <Zap className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-2">Instant Results</h3>
              <p className="text-sm text-muted-foreground">
                No waiting, no processing queues. Every tool delivers results instantly as you work.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-muted/30 border border-border">
              <CheckCircle2 className="w-8 h-8 text-accent mb-3" />
              <h3 className="font-semibold mb-2">Privacy Guaranteed</h3>
              <p className="text-sm text-muted-foreground">
                Your data never leaves your browser. We don't collect, store, or transmit anything you input.
              </p>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full max-w-3xl">
              {page.faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="border-border">
                  <AccordionTrigger className="text-left hover:no-underline">
                    <span className="font-medium">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Related Blog Posts */}
          {blogPosts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mb-16"
            >
              <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
                Related Articles
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {blogPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group p-4 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
                  >
                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}
              </div>
            </motion.div>
          )}

          {/* Other Comparisons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans']">
              More Tool Comparisons
            </h2>
            <div className="flex flex-wrap gap-3">
              {Object.values(comparisonPages)
                .filter(p => p.slug !== slug)
                .map((comparison) => {
                  const Icon = iconMap[comparison.icon] || Wrench;
                  return (
                    <Link
                      key={comparison.slug}
                      to={`/compare/${comparison.slug}`}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:border-amber-500/50 hover:bg-card/80 transition-all"
                    >
                      <Icon className="w-4 h-4 text-amber-500" />
                      <span>{comparison.heroTitle.replace(' 2026', '')}</span>
                    </Link>
                  );
                })}
              <Link
                to="/all-tools"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors"
              >
                View All Tools
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
            className="text-center p-8 rounded-2xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20"
          >
            <h2 className="text-2xl font-bold mb-3 font-['Plus_Jakarta_Sans']">
              Ready to Try These Tools?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Start using any of these tools right now. No signup required, completely free.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to={`/tools/${tools[0]?.slug || 'password-generator'}`}>
                <Button className="bg-amber-500 hover:bg-amber-600">
                  <Trophy className="w-4 h-4 mr-2" />
                  Try #{tools[0]?.name || 'Top Tool'}
                </Button>
              </Link>
              <Link to="/all-tools">
                <Button variant="outline">
                  Browse All Tools
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default ComparisonPage;
